import TransparentVideo from 'components/reusable/transparent-video';
import { FC, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

interface ViaCapVideoPlayerProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  fallbackSrc: string;
  /** Still frame shown in place of the animated fallback while paused. */
  fallbackStillSrc?: string;
  paused?: boolean;
}

export const ViaCapVideoPlayer: FC<ViaCapVideoPlayerProps> = ({
  src,
  fallbackSrc,
  fallbackStillSrc,
  paused = false,
  alt,
  width,
  height,
  loop = true,
  muted = true,
  playsInline = true,
}) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [inViewRef, inView] = useInView();

  const setRefs = (node: HTMLVideoElement | null) => {
    ref.current = node;
    inViewRef(node);
  };

  useEffect(() => {
    // Turn off animations for visual testing
    if (typeof window !== 'undefined' && (window as any).disableAnimations) {
      return;
    }

    if (paused) {
      ref.current?.pause();
      return;
    }

    let timeout: NodeJS.Timeout;
    if (inView) {
      timeout = setTimeout(() => {
        ref.current?.play()?.catch(() => undefined);
      }, 1000);
    }
    return () => clearTimeout(timeout);
  }, [inView, paused]);

  return (
    <TransparentVideo
      ref={setRefs}
      src={src}
      fallbackSrc={paused && fallbackStillSrc ? fallbackStillSrc : fallbackSrc}
      width={width}
      height={height}
      loop={loop}
      muted={muted}
      playsInline={playsInline}
      aria-label={alt}
      alt={alt}
      data-testid='viacap-video'
      style={{
        objectFit: 'contain',
      }}
    />
  );
};
